import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Phone, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ResponseTimePromise } from "./ResponseTimePromise";
import { trackEvent } from "@/lib/analytics";

const SESSION_KEY = "graystone_exit_intent_shown";

export function ExitIntentModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Desktop only. Never fire on touch devices or narrow (mobile) viewports.
    const hasFinePointer = window.matchMedia?.("(pointer: fine)").matches ?? false;
    const isWideEnough = window.innerWidth >= 1024;
    if (!hasFinePointer || !isWideEnough) return;

    // Show at most once per browser session.
    if (sessionStorage.getItem(SESSION_KEY) === "true") return;

    const handleMouseLeave = (event: MouseEvent) => {
      // Only treat an upward exit past the top of the viewport as intent to leave.
      if (event.clientY > 0) return;
      if (sessionStorage.getItem(SESSION_KEY) === "true") return;

      sessionStorage.setItem(SESSION_KEY, "true");
      setOpen(true);
      trackEvent("exit_intent_modal_shown", { event_category: "cro" });
      document.removeEventListener("mouseleave", handleMouseLeave);
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="exit-intent-modal">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center font-serif text-2xl">
            Before you go, get a free inspection
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            Schedule a free, no-obligation roof or siding inspection with our team. We will
            show you exactly what your home needs, with no pressure and no hidden fees.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <a
            href="tel:7177257170"
            className="flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 font-bold text-white transition-colors hover:bg-primary/90"
            data-testid="exit-intent-call"
            onClick={() => trackEvent("exit_intent_call", { event_category: "cro" })}
          >
            <Phone className="h-5 w-5" />
            Call 717-725-7170
          </a>
          <Button asChild variant="outline" size="lg">
            <Link
              href="/contact-graystone-roofing-siding/"
              data-testid="exit-intent-contact"
              onClick={() => setOpen(false)}
            >
              Request my free inspection
            </Link>
          </Button>
        </div>

        <ResponseTimePromise />
      </DialogContent>
    </Dialog>
  );
}
